import { useEffect, useMemo, useState } from 'react'
import { X, Loader2, FileText, Download, ExternalLink, Plus, Trash2, AlertTriangle, CheckCircle2 } from 'lucide-react'
import { postJson } from '../../lib/api.js'
import { useMe } from '../../lib/me.js'
import { fullName, digitsOnly } from '../../lib/employee.js'
import {
  EMPLOYMENT, CONTRACT_TYPES, CONTRACT_POSITIONS, DEFAULT_CONTRACT, contractLabel, contractPositionFor,
  seedForm, buildPayload, validate, warnings, emptyStep, fmtDateFullTH, IDCARD_LEN, PHONE_LEN,
} from '../../lib/contract.js'
import SearchableSelect from '../SearchableSelect.jsx'
import LockedInput from '../LockedInput.jsx'

const FIELD = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'
const LABEL = 'block text-[12.5px] font-medium text-slate-500 mb-1'

const LOCKED_HINT = 'ข้อมูลระบุตัวตนมาจากประวัติพนักงาน — หากไม่ถูกต้อง ให้แก้ที่ประวัติพนักงานก่อนออกสัญญา'
const POSITION_OPTS = [
  { value: '', label: '— เลือกตำแหน่งตามสัญญา —' },
  ...CONTRACT_POSITIONS.map((p) => ({ value: p, label: p })),
]

/**
 * ออกสัญญา — fills the two Google Docs contract templates from an employee record
 * and returns a PDF to download. Nothing is stored: this generates a document,
 * it does not record that a contract exists. Signing and filing stay on paper
 * until there is somewhere to put them.
 *
 * Every field arrives prefilled and every field stays editable. The record is
 * the best first guess at a contract, never the contract itself — the salary
 * being offered, the address on the day of signing and the wording of the
 * position are all things HR settles at the desk, not things the CRM knows.
 */
export default function ContractDialog({ employee, branches, onClose }) {
  const me = useMe()
  const branch = useMemo(
    () => (branches || []).find((b) => Number(b.id) === Number(employee?.branch_id)) || null,
    [branches, employee?.branch_id],
  )

  const [type, setType] = useState(DEFAULT_CONTRACT)
  const [form, setForm] = useState(() => seedForm(employee, branch, me?.name))
  // Whether HR has typed over the auto-filled name. Once they have, the effect
  // below must never overwrite it — the signature line is theirs to set, and the
  // person generating a contract is not always the person signing it.
  const [hrTouched, setHrTouched] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  // Either { fileName, pdfBase64 } — the bytes, which is the path we want — or
  // { fileName, url, downloadUrl } when the Apps Script ignored return_pdf and
  // only wrote a Drive file. See functions/api/hr/contract.js.
  const [done, setDone] = useState(null)

  // A blob: URL for the returned PDF, so the download never goes through a
  // public Drive link. Revoked when the dialog closes or another contract is
  // generated — an un-revoked object URL keeps the whole PDF in memory.
  const blobUrl = useMemo(() => {
    if (!done?.pdfBase64) return ''
    try {
      const bin = atob(done.pdfBase64)
      const bytes = new Uint8Array(bin.length)
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
      return URL.createObjectURL(new Blob([bytes], { type: done.mimeType || 'application/pdf' }))
    } catch { return '' }
  }, [done])
  useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl) }, [blobUrl])

  // เจ้าหน้าที่ฝ่ายทรัพยากรบุคคล defaults to the signed-in user. seedForm() already
  // takes me?.name, but that runs in a lazy useState initialiser — ONCE, on the
  // first render — and useMe() returns null until /__app/me resolves. On a cold
  // load that left the field empty with no second chance, so it is filled in here
  // when `me` lands as well. Only while untouched and only while still empty.
  useEffect(() => {
    if (hrTouched) return
    const who = String(me?.name || '').trim()
    if (!who) return
    setForm((f) => (f.hr_name.trim() ? f : { ...f, hr_name: who }))
  }, [me, hrTouched])

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e
    setForm((f) => ({ ...f, [k]: v }))
    setErr('')
  }

  const notes = useMemo(() => warnings(type, form), [type, form])
  // The CRM position that has no contract wording is worth saying out loud here,
  // because the field simply comes up empty and an empty dropdown reads as
  // "nothing to choose" rather than "we could not translate your job title".
  const unmapped = employee?.position && !contractPositionFor(employee.position)

  function stepSet(i, k, v) {
    setForm((f) => {
      const steps = [...(f.salary_steps || [])]
      steps[i] = { ...steps[i], [k]: v }
      return { ...f, salary_steps: steps }
    })
    setErr('')
  }
  const stepAdd = () => setForm((f) => ({ ...f, salary_steps: [...(f.salary_steps || []), emptyStep()] }))
  const stepDel = (i) => setForm((f) => ({ ...f, salary_steps: (f.salary_steps || []).filter((_, j) => j !== i) }))

  async function generate() {
    const bad = validate(type, form)
    if (bad) { setErr(bad); return }
    setBusy(true); setErr('')
    try {
      const res = await postJson('/api/hr/contract', { type, ...buildPayload(type, form) })
      // Belt to the proxy's braces. The success screen is the only signal HR gets
      // that a contract exists, so it is shown only when there is actually a file
      // behind it — never on the strength of a 200 alone.
      if (!res?.pdfBase64 && !res?.downloadUrl && !res?.url) {
        setErr('สร้างเอกสารไม่สำเร็จ — ไม่ได้รับไฟล์กลับมา กรุณาลองใหม่')
        return
      }
      setDone(res)
    } catch (e) {
      // api.js hands the parsed error body over as `e.body`. Every refusal this
      // proxy makes carries a Thai `detail` that IS the explanation — showing
      // `e.message` instead puts "HTTP 500: {"error":…}" in front of HR, who can
      // do nothing with it. Fall back to the raw message only when there is no body.
      setErr(e?.body?.detail || e?.body?.error || String(e.message || e))
    }
    finally { setBusy(false) }
  }

  const isEmployment = type === EMPLOYMENT

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onMouseDown={onClose} />
      <div className="relative w-full max-w-[620px] max-h-[92vh] flex flex-col bg-white rounded-2xl shadow-2xl ring-1 ring-slate-200 animate-ss-pop-in">

        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <FileText size={17} className="text-brand-600 shrink-0" />
            <h2 className="text-[15.5px] font-semibold text-slate-900 truncate">
              ออกสัญญา · {fullName(employee)}
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 shrink-0"><X size={18} /></button>
        </div>

        {done ? (
          <div className="px-5 py-9 text-center">
            <CheckCircle2 size={40} className="mx-auto text-emerald-500 mb-3" />
            <div className="text-[15px] font-semibold text-slate-800">สร้าง{contractLabel(type)}เรียบร้อย</div>
            <p className="text-[12.5px] text-slate-500 mt-1 break-all px-4">{done.fileName || ''}</p>
            <div className="mt-5 flex items-center justify-center gap-2">
              {blobUrl ? (
                <>
                  <a href={blobUrl} download={done.fileName || 'contract.pdf'}
                    className="px-4 py-2 rounded-xl text-[13.5px] font-semibold text-white bg-brand-600 hover:bg-brand-700 inline-flex items-center gap-1.5">
                    <Download size={15} /> ดาวน์โหลด PDF
                  </a>
                  <a href={blobUrl} target="_blank" rel="noreferrer"
                    className="px-4 py-2 rounded-xl text-[13.5px] font-medium text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50 inline-flex items-center gap-1.5">
                    <ExternalLink size={15} /> เปิดดู
                  </a>
                </>
              ) : (
                <>
                  {done.downloadUrl && (
                    <a href={done.downloadUrl} target="_blank" rel="noreferrer"
                      className="px-4 py-2 rounded-xl text-[13.5px] font-semibold text-white bg-brand-600 hover:bg-brand-700 inline-flex items-center gap-1.5">
                      <Download size={15} /> ดาวน์โหลด PDF
                    </a>
                  )}
                  {done.url && (
                    <a href={done.url} target="_blank" rel="noreferrer"
                      className="px-4 py-2 rounded-xl text-[13.5px] font-medium text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50 inline-flex items-center gap-1.5">
                      <ExternalLink size={15} /> เปิดดู
                    </a>
                  )}
                </>
              )}
            </div>
            {!blobUrl && (
              <p className="mt-3 text-[11.5px] text-amber-600 px-6">
                ไฟล์นี้ถูกเก็บใน Google Drive แบบ "ใครมีลิงก์ก็เปิดได้" — เอกสารมีเลขบัตรประชาชนและเงินเดือน กรุณาอย่าส่งต่อลิงก์
              </p>
            )}
            <button onClick={() => setDone(null)} className="mt-4 text-[12.5px] text-slate-500 hover:text-slate-700 underline underline-offset-2">
              ออกอีกฉบับ
            </button>
          </div>
        ) : (
          <>
            <div className="px-5 py-4 overflow-y-auto space-y-5">

              {/* type */}
              <div className="grid grid-cols-2 gap-2">
                {CONTRACT_TYPES.map((c) => (
                  <button key={c.key} type="button" onClick={() => { setType(c.key); setErr('') }}
                    className={`px-3 py-2.5 rounded-xl text-[13.5px] font-semibold ring-1 transition ${
                      type === c.key
                        ? 'bg-brand-50 text-brand-700 ring-brand-200'
                        : 'bg-white text-slate-500 ring-slate-200 hover:bg-slate-50'}`}>
                    {c.label}
                  </button>
                ))}
              </div>

              <Section title="วันที่">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="วันที่ทำสัญญา" required hint={fmtDateFullTH(form.date)}>
                    <input type="date" value={form.date} onChange={set('date')} className={FIELD} />
                  </Field>
                  {isEmployment ? (
                    <Field label="วันเริ่มงาน" hint={fmtDateFullTH(form.date_start)}>
                      <input type="date" value={form.date_start} onChange={set('date_start')} className={FIELD} />
                    </Field>
                  ) : (
                    <Field label="วันเริ่มฝึกอบรม" required hint={fmtDateFullTH(form.date_start)}>
                      <input type="date" value={form.date_start} onChange={set('date_start')} className={FIELD} />
                    </Field>
                  )}
                  {!isEmployment && (
                    <Field label="วันสิ้นสุดการฝึกอบรม" required hint={fmtDateFullTH(form.date_end)}>
                      <input type="date" value={form.date_end} onChange={set('date_end')} className={FIELD} />
                    </Field>
                  )}
                </div>
              </Section>

              <Section title={isEmployment ? 'ผู้ว่าจ้าง' : 'ผู้จัดอบรม'}>
                <div className="space-y-3">
                  <Field label="ชื่อบริษัท" required>
                    <input value={form.company_name} onChange={set('company_name')} className={FIELD} placeholder="บริษัท …" />
                  </Field>
                  <Field label="ที่อยู่บริษัท" required>
                    <textarea value={form.company_address} onChange={set('company_address')} rows={2} className={FIELD} />
                  </Field>
                  {isEmployment && (
                    <Field label="ทะเบียนนิติบุคคล" required>
                      <input value={form.company_taxid} onChange={set('company_taxid')} className={FIELD} inputMode="numeric" />
                    </Field>
                  )}
                </div>
              </Section>

              {/* Identity is READ-ONLY here. คำนำหน้า / ชื่อ-สกุล / เลขบัตรประชาชน / เบอร์โทร are
                  who the person IS, and the contract must name the same person the
                  personnel record does — a เลขบัตรประชาชน retyped for one PDF produces a
                  signed document that disagrees with the record it was generated from,
                  and nothing would ever reconcile the two. Wrong here means wrong on the
                  record: close this and fix it there. Everything below stays editable —
                  the address on the day of signing, the contract wording of the position
                  and the salary being offered are all things HR settles at the desk. */}
              <Section title={isEmployment ? 'พนักงาน' : 'ผู้รับการอบรม'}>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="คำนำหน้า">
                    <LockedInput value={form.employee_prefix} placeholder="—" title={LOCKED_HINT} />
                  </Field>
                  <Field label="ชื่อ-สกุล" required>
                    <LockedInput value={form.employee_name} title={LOCKED_HINT} />
                  </Field>
                  <Field label="เลขบัตรประชาชน" required aside={<Count value={form.employee_idcard} len={IDCARD_LEN} />}>
                    <LockedInput value={form.employee_idcard} title={LOCKED_HINT} />
                  </Field>
                  <Field label="เบอร์โทร" required aside={<Count value={form.employee_phone} len={PHONE_LEN} />}>
                    <LockedInput value={form.employee_phone} title={LOCKED_HINT} />
                  </Field>
                  <div className="col-span-2 -mt-1">
                    <p className="text-[11.5px] text-slate-400">{LOCKED_HINT}</p>
                  </div>
                  {isEmployment && (
                    <>
                      <div className="col-span-2">
                        <Field label="ที่อยู่ปัจจุบัน" required>
                          <textarea value={form.employee_address} onChange={set('employee_address')} rows={2} className={FIELD} />
                        </Field>
                      </div>
                      <div className="col-span-2">
                        <Field
                          label="ตำแหน่งตามสัญญา" required
                          hint={unmapped
                            ? `ตำแหน่งในระบบคือ "${employee.position}" ซึ่งยังไม่มีข้อความ ข้อ 5 ในสัญญา — กรุณาเลือกให้ตรงกับหน้าที่จริง`
                            : 'กำหนดข้อความใน ข้อ 5 หน้าที่และความรับผิดชอบ'}>
                          <SearchableSelect options={POSITION_OPTS} value={form.employee_position} onChange={set('employee_position')} placeholder="— เลือกตำแหน่งตามสัญญา —" />
                        </Field>
                      </div>
                    </>
                  )}
                </div>
              </Section>

              {isEmployment && (
                <>
                  <Section title="สถานที่ปฏิบัติงาน">
                    <div className="space-y-3">
                      <Field label="ชื่อสาขา" required>
                        <input value={form.clinic_name} onChange={set('clinic_name')} className={FIELD} />
                      </Field>
                      <Field label="ที่อยู่สาขา" required>
                        <textarea value={form.clinic_address} onChange={set('clinic_address')} rows={2} className={FIELD} />
                      </Field>
                    </div>
                  </Section>

                  <Section title="เงินเดือน" note="ข้อ 3.1 — ระบุเป็นขั้นตามเดือนที่ทำงาน">
                    <div className="space-y-2">
                      {(form.salary_steps || []).map((s, i) => (
                        <div key={i} className="flex items-end gap-2">
                          <div className="w-[88px]">
                            <label className={LABEL}>เดือนที่</label>
                            <input value={s.from} onChange={(e) => stepSet(i, 'from', digitsOnly(e.target.value).slice(0, 3))}
                              className={FIELD} inputMode="numeric" placeholder="1" />
                          </div>
                          <div className="w-[88px]">
                            <label className={LABEL}>ถึงเดือนที่</label>
                            <input value={s.to} onChange={(e) => stepSet(i, 'to', digitsOnly(e.target.value).slice(0, 3))}
                              className={FIELD} inputMode="numeric" placeholder="ตลอด" />
                          </div>
                          <div className="flex-1">
                            <label className={LABEL}>เงินเดือน (บาท)</label>
                            <input value={s.amount} onChange={(e) => stepSet(i, 'amount', digitsOnly(e.target.value).slice(0, 8))}
                              className={FIELD} inputMode="numeric" />
                          </div>
                          <button type="button" onClick={() => stepDel(i)} disabled={(form.salary_steps || []).length <= 1}
                            className="p-2 mb-0.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400">
                            <Trash2 size={15} />
                          </button>
                        </div>
                      ))}
                      <button type="button" onClick={stepAdd}
                        className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-brand-600 hover:text-brand-700 px-1 py-1">
                        <Plus size={14} /> เพิ่มขั้นเงินเดือน
                      </button>
                      <p className="text-[11.5px] text-slate-400">
                        เว้น "ถึงเดือนที่" ว่างไว้ = อัตรานี้ใช้ตั้งแต่เดือนนั้นเป็นต้นไป · ขั้นเดียวจะเขียนรวมในบรรทัด 3.1
                      </p>
                    </div>
                  </Section>
                </>
              )}

              <Section title="ผู้ออกสัญญา">
                <Field label="เจ้าหน้าที่ฝ่ายทรัพยากรบุคคล" required hint="ชื่อที่ปรากฏบนบรรทัดลงนามฝั่งคลินิก">
                  <input
                    value={form.hr_name}
                    onChange={(e) => { setHrTouched(true); set('hr_name')(e) }}
                    className={FIELD}
                  />
                </Field>
              </Section>

              {notes.length > 0 && (
                <div className="rounded-xl bg-amber-50 ring-1 ring-amber-200 px-3.5 py-2.5 space-y-1">
                  {notes.map((n, i) => (
                    <div key={i} className="flex items-start gap-2 text-[12.5px] text-amber-800">
                      <AlertTriangle size={14} className="mt-0.5 shrink-0" /><span>{n}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Errors wrap here rather than being truncated on the footer line.
                  A validation message is short, but the ones that come back from
                  the renderer are a full sentence naming what to go and fix — cut
                  at the fold they say nothing at all. */}
              {err && (
                <div className="rounded-xl bg-rose-50 ring-1 ring-rose-200 px-3.5 py-2.5 flex items-start gap-2">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0 text-rose-500" />
                  <span className="text-[12.5px] text-rose-700 break-words">{err}</span>
                </div>
              )}
            </div>

            <div className="border-t border-slate-100 px-5 py-3.5 flex items-center justify-end gap-2 shrink-0">
              <button onClick={onClose} className="px-4 py-2 rounded-xl text-[13.5px] font-medium text-slate-600 hover:bg-slate-100">ยกเลิก</button>
              <button onClick={generate} disabled={busy}
                className="px-4 py-2 rounded-xl text-[13.5px] font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50 inline-flex items-center gap-1.5">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} สร้าง PDF
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

/* ── local bits ──────────────────────────────────────────────────────────── */

function Section({ title, note, children }) {
  return (
    <div>
      <div className="flex items-baseline gap-2 mb-2.5">
        <h3 className="text-[13px] font-semibold text-slate-700">{title}</h3>
        {note && <span className="text-[11.5px] text-slate-400">{note}</span>}
      </div>
      {children}
    </div>
  )
}

function Field({ label, hint, required, aside, children }) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <label className={LABEL}>{label}{required && <span className="text-rose-500"> *</span>}</label>
        {aside}
      </div>
      {children}
      {hint && <p className="text-[11.5px] text-slate-400 mt-0.5">{hint}</p>}
    </div>
  )
}

function Count({ value, len }) {
  const n = String(value || '').length
  return <span className={`text-[11.5px] tabular-nums ${n === len ? 'text-slate-400' : 'text-amber-600'}`}>{n}/{len}</span>
}
